import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PDFService } from '@/services/knowledgebase/PDFService';
import { DocumentWithMetadata } from '@/types/knowledge/knowledge-base';

export interface ParsingStatus {
  isCompleted: boolean;
  isProcessing: boolean;
  progress: number;
  hasMetadata: boolean;
  needsRetry: boolean;
}

const shouldProcessDocument = (document: DocumentWithMetadata): boolean => {
  const isPdf = PDFService.isPdf(document);

  // Include PDFs even without URL yet (URL is set on completion)
  // Also include documents with S3 URLs
  if (!document.source_path) return isPdf;

  const hasS3Url = document.source_path.includes("s3.");

  return hasS3Url || isPdf;
};

// Global realtime progress state shared across all hook instances
let globalRealtimeProgress: Record<string, number> = (() => {
  if (typeof window === "undefined") return {};
  try {
    const stored = localStorage.getItem("documentParsingProgress");
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
})();

const realtimeProgressListeners = new Set<(progress: Record<string, number>) => void>();

const notifyRealtimeProgressListeners = () => {
  realtimeProgressListeners.forEach((listener) => listener({ ...globalRealtimeProgress }));
};

const persistRealtimeProgress = () => {
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem("documentParsingProgress", JSON.stringify(globalRealtimeProgress));
    } catch {
      // Ignore storage errors
    }
  }
};

const subscribeToRealtimeProgress = (callback: (progress: Record<string, number>) => void) => {
  realtimeProgressListeners.add(callback);
  return () => {
    realtimeProgressListeners.delete(callback);
  };
};

// Public function to update progress from external sources (e.g., GlobalRealtimeListeners)
export const updateDocumentParsingProgress = (documentId: string, progress: number) => {
  globalRealtimeProgress[documentId] = progress;
  persistRealtimeProgress();
  notifyRealtimeProgressListeners();
};

// Remove duplicate channel - useDocumentRealtime already listens to these events
// This hook will receive updates via the updateDocumentParsingProgress function
// which is called by other parts of the app that handle the realtime updates

export const useDocumentParsingStatus = (documents: DocumentWithMetadata[]) => {
  const [processingDocuments, setProcessingDocuments] = useState<Set<string>>(new Set());
  const [retryingDocuments, setRetryingDocuments] = useState<Set<string>>(new Set());
  const [realtimeProgress, setRealtimeProgress] = useState<Record<string, number>>(() => ({
    ...globalRealtimeProgress,
  }));
  const queryClient = useQueryClient();

  // Subscribe to global realtime progress updates
  useEffect(() => {
    return subscribeToRealtimeProgress((progress) => {
      setRealtimeProgress(progress);
    });
  }, []);

  const checkParsingStatus = (document: DocumentWithMetadata): ParsingStatus => {
    if (!shouldProcessDocument(document)) {
      return {
        isCompleted: true,
        isProcessing: false,
        progress: 100,
        hasMetadata: false,
        needsRetry: false,
      };
    }

    const metadata = document.metadata;
    const parsingStatus = metadata?.parsing_status;
    const status = metadata?.status;
    const parsed = metadata?.parsed;

    // Check if parsing is completed from metadata first — this is authoritative and
    // must win over a stale realtime progress value left behind by a dropped final event.
    const isCompletedFromMetadata =
      parsingStatus === "completed" || status === "completed" || parsed === true;

    if (isCompletedFromMetadata) {
      if (globalRealtimeProgress[document.id] !== undefined) {
        delete globalRealtimeProgress[document.id];
        persistRealtimeProgress();
      }
      return {
        isCompleted: true,
        isProcessing: false,
        progress: 100,
        hasMetadata: true,
        needsRetry: false,
      };
    }

    // Check if we have realtime progress for this document (only relevant while not yet completed)
    const realtimeProgressValue = realtimeProgress[document.id];
    if (realtimeProgressValue !== undefined) {
      const isCompleted = realtimeProgressValue === 100;
      return {
        isCompleted,
        isProcessing: !isCompleted,
        progress: realtimeProgressValue,
        hasMetadata: true,
        needsRetry: false,
      };
    }

    // Check if parsing is in progress from metadata
    const isInProgress = parsingStatus === "in_progress" || status === "in_progress";

    // For new documents without metadata status, assume they're processing
    const createdAt = new Date(document.created_at);
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    const isVeryRecent = createdAt > fiveMinutesAgo;
    const hasNoStatus = !parsingStatus && !status && !parsed;

    // If document is very recent and has no status, assume it's being processed
    if (isVeryRecent && hasNoStatus) {
      return {
        isCompleted: false,
        isProcessing: true,
        progress: 0,
        hasMetadata: !!metadata,
        needsRetry: false,
      };
    }

    // Determine if document needs retry (old documents that are not completed and not in progress)
    const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000);
    const isOlderThan2Hours = createdAt < twoHoursAgo;
    const needsRetry = isOlderThan2Hours && !isCompletedFromMetadata && !isInProgress;

    return {
      isCompleted: false,
      isProcessing: isInProgress,
      progress: 0,
      hasMetadata: !!metadata,
      needsRetry,
    };
  };

  const { data: documentParsingStatus = {} } = useQuery({
    queryKey: [
      "documentParsingStatus",
      documents.map((d) => ({ id: d.id, metadata: d.metadata })),
      realtimeProgress,
    ],
    queryFn: () => {
      const applicableDocuments = documents.filter(shouldProcessDocument);
      const statusMap: Record<string, ParsingStatus> = {};

      applicableDocuments.forEach((doc) => {
        const status = checkParsingStatus(doc);
        statusMap[doc.id] = status;

        // Clean up progress from storage when document is completed
        if (status.isCompleted && globalRealtimeProgress[doc.id] === 100) {
          delete globalRealtimeProgress[doc.id];
          persistRealtimeProgress();
        }
      });

      return statusMap;
    },
    enabled: documents.length > 0,
    refetchInterval: false, // Disable polling, rely on realtime updates
    staleTime: 0, // Allow refetch when document metadata changes
  });

  const retryParsing = async (document: DocumentWithMetadata) => {
    if (!document.source_path || retryingDocuments.has(document.id)) return;

    setRetryingDocuments((prev) => new Set(prev).add(document.id));
    const retryToast = toast.loading("Retrying PDF parsing...");

    try {
      await PDFService.retryExtraction(document.source_path, document.id, null);
      toast.success("PDF parsing restarted successfully", { id: retryToast });

      queryClient.invalidateQueries({ queryKey: ["documentParsingStatus"] });
    } catch (error: any) {
      console.error("Error retrying PDF extraction:", error);
      toast.error(`Failed to retry parsing: ${error.message || "Unknown error"}`, {
        id: retryToast,
      });
    } finally {
      setRetryingDocuments((prev) => {
        const newSet = new Set(prev);
        newSet.delete(document.id);
        return newSet;
      });
    }
  };

  const handleAutomaticSendToElevenLabs = async (
    document: DocumentWithMetadata,
    sendToElevenLabs: (doc: DocumentWithMetadata) => void | Promise<void>,
  ) => {
    if (processingDocuments.has(document.id)) return;

    setProcessingDocuments((prev) => new Set(prev).add(document.id));

    try {
      await sendToElevenLabs(document);
    } catch (error) {
      console.error("Error automatically sending to El KB:", error);
    } finally {
      setProcessingDocuments((prev) => {
        const newSet = new Set(prev);
        newSet.delete(document.id);
        return newSet;
      });
    }
  };

  return {
    documentParsingStatus,
    processingDocuments,
    retryingDocuments,
    retryParsing,
    handleAutomaticSendToElevenLabs,
    checkParsingStatus,
  };
};
